import { ParseKeys } from "i18next";
import { VendorVerification } from "generated/graphql";

// Mirrors ONBOARDING_STEPS in ev-server's vendor.resolver.ts.
export type OnboardingStep =
  | "documents"
  | "agreement"
  | "brand_identity"
  | "availability"
  | "services"
  | "done";

export const STEP_LABELS: Record<
  Exclude<OnboardingStep, "done">,
  ParseKeys<"onboarding">
> = {
  documents: "steps.documents",
  agreement: "steps.agreement",
  brand_identity: "steps.brand_identity",
  availability: "steps.availability",
  services: "steps.services",
};

interface OnboardingVendor {
  offeringEvInstallation: string;
  insuranceExpiryDate?: string | null;
  electricalLicenseExpiryDate?: string | null;
  agreementAcceptedAt?: string | null;
  onboardingStep?: string | null;
  verificationStatus?: VendorVerification;
}

/**
 * Expiry dates that vendors who registered before they were collected still
 * owe. Until they are added the vendor dashboard stays on onboarding.
 */
export const missingRequiredDocuments = (vendor: OnboardingVendor): boolean =>
  !vendor.insuranceExpiryDate ||
  (vendor.offeringEvInstallation === "Yes" &&
    !vendor.electricalLicenseExpiryDate);

/** The steps this vendor goes through, in order. */
export const stepsFor = (
  vendor: OnboardingVendor,
): Exclude<OnboardingStep, "done">[] => [
  ...(missingRequiredDocuments(vendor) ? (["documents"] as const) : []),
  "agreement",
  "brand_identity",
  "availability",
  // Installation is the only service type, so others have nothing to add yet.
  ...(vendor.offeringEvInstallation === "Yes" ? (["services"] as const) : []),
];

/** Where to resume: required steps first, then the saved step. */
export const currentStep = (vendor: OnboardingVendor): OnboardingStep => {
  if (missingRequiredDocuments(vendor)) {
    return "documents";
  }
  if (!vendor.agreementAcceptedAt) {
    return "agreement";
  }
  if (vendor.onboardingStep === "done") {
    return "done";
  }
  const steps = stepsFor(vendor);
  const saved = steps.find((step) => step === vendor.onboardingStep);
  return saved ?? steps.find((step) => step !== "agreement") ?? "done";
};

/** The step after `step`, or "done" after the last one. */
export const nextStep = (
  vendor: OnboardingVendor,
  step: OnboardingStep,
): OnboardingStep => {
  const steps = stepsFor(vendor);
  return steps[steps.indexOf(step as (typeof steps)[number]) + 1] ?? "done";
};

/** Whether onboarding has a step to do now; services wait for approval. */
export const onboardingOpen = (vendor: OnboardingVendor): boolean => {
  const step = currentStep(vendor);
  return (
    step !== "done" &&
    !(
      step === "services" &&
      vendor.verificationStatus !== VendorVerification.Approved
    )
  );
};
